/**
 * BestRunningTimes - Shows optimal running times based on AQ forecast
 * Blurp.ie unique feature
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { useLocationStore } from '@/stores/useLocationStore';
import { cn } from '@/utils/cn';

export function BestRunningTimes() {
  const bestRunningTimes = useRunningStore((state) => state.bestRunningTimes);
  const fetchBestRunningTimes = useRunningStore((state) => state.fetchBestRunningTimes);
  const currentPosition = useLocationStore((state) => state.currentPosition);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTimes = async () => {
      if (currentPosition) {
        setIsLoading(true);
        await fetchBestRunningTimes(currentPosition.lat, currentPosition.lng);
        setIsLoading(false);
      }
    };
    loadTimes();
  }, [currentPosition, fetchBestRunningTimes]);

  // Get the best time slots (excellent and good recommendations)
  const bestSlots = bestRunningTimes
    .filter((t) => t.recommendation === 'excellent' || t.recommendation === 'good')
    .slice(0, 3);

  const nextBestSlot = bestSlots[0];

  const getRecommendationColor = (rec: string): string => {
    switch (rec) {
      case 'excellent':
        return 'text-emerald-400';
      case 'good':
        return 'text-cyan-400';
      case 'moderate':
        return 'text-yellow-400';
      case 'avoid':
        return 'text-red-400';
      default:
        return 'text-text-secondary';
    }
  };

  const getRecommendationBg = (rec: string): string => {
    switch (rec) {
      case 'excellent':
        return 'bg-emerald-500/20 border-emerald-500/30';
      case 'good':
        return 'bg-cyan-500/20 border-cyan-500/30';
      case 'moderate':
        return 'bg-yellow-500/20 border-yellow-500/30';
      case 'avoid':
        return 'bg-red-500/20 border-red-500/30';
      default:
        return 'bg-slate-800/50 border-glass-border';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-glass-bg backdrop-blur-xl border border-glass-border rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full"
            />
          </div>
          <div>
            <div className="text-white font-medium">Analyzing air quality...</div>
            <div className="text-xs text-text-secondary">Finding best running times</div>
          </div>
        </div>
      </div>
    );
  }

  if (!nextBestSlot) {
    return (
      <div className="bg-glass-bg backdrop-blur-xl border border-glass-border rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
            <span className="text-xl">⚠️</span>
          </div>
          <div>
            <div className="text-white font-medium">Poor air quality today</div>
            <div className="text-xs text-text-secondary">Consider indoor exercise</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-glass-bg backdrop-blur-xl border border-glass-border rounded-2xl overflow-hidden">
      {/* Collapsed View */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center gap-3 text-left"
      >
        <div className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center',
          nextBestSlot.recommendation === 'excellent' ? 'bg-emerald-500/20' : 'bg-cyan-500/20'
        )}>
          <span className="text-2xl">☁️</span>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium">Best time to run</span>
            <span className={cn(
              'px-2 py-0.5 rounded-full text-xs font-medium capitalize',
              getRecommendationBg(nextBestSlot.recommendation),
              getRecommendationColor(nextBestSlot.recommendation)
            )}>
              {nextBestSlot.recommendation}
            </span>
          </div>
          <div className="text-xs text-text-secondary mt-0.5">
            {nextBestSlot.time_slot} • AQI {nextBestSlot.predicted_aqi} • {nextBestSlot.temperature}°C
          </div>
        </div>
        <motion.span
          animate={{ rotate: isExpanded ? 180 : 0 }}
          className="text-text-secondary"
        >
          ▼
        </motion.span>
      </button>

      {/* Expanded View */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-2">
              <div className="text-xs text-text-secondary mb-2">Next 12 hours</div>

              {bestRunningTimes.slice(0, 12).map((slot, index) => (
                <motion.div
                  key={slot.time_slot}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className={cn(
                    'flex items-center gap-3 p-2 rounded-lg border',
                    getRecommendationBg(slot.recommendation)
                  )}
                >
                  {/* Time */}
                  <div className="w-12 text-center">
                    <div className="text-white font-mono text-sm">{slot.time_slot}</div>
                  </div>

                  {/* Recommendation Icon */}
                  <div className="w-8">
                    {slot.recommendation === 'excellent' && <span>🌟</span>}
                    {slot.recommendation === 'good' && <span>👍</span>}
                    {slot.recommendation === 'moderate' && <span>⚠️</span>}
                    {slot.recommendation === 'avoid' && <span>🚫</span>}
                  </div>

                  {/* Stats */}
                  <div className="flex-1 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className={getRecommendationColor(slot.recommendation)}>
                        AQI {slot.predicted_aqi}
                      </span>
                    </div>
                    <div className="text-text-secondary">
                      {slot.temperature}°C
                    </div>
                    <div className="text-text-secondary">
                      UV {slot.uv_index}
                    </div>
                  </div>

                  {/* Score */}
                  <div className="w-12 text-right">
                    <div className={cn(
                      'text-sm font-bold',
                      getRecommendationColor(slot.recommendation)
                    )}>
                      {slot.recommendation_score}
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Legend */}
              <div className="pt-3 border-t border-glass-border">
                <div className="text-xs text-text-secondary mb-2">Recommendation Guide</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span>🌟</span>
                    <span className="text-emerald-400">Excellent</span>
                    <span className="text-text-secondary">- Perfect conditions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>👍</span>
                    <span className="text-cyan-400">Good</span>
                    <span className="text-text-secondary">- Recommended</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>⚠️</span>
                    <span className="text-yellow-400">Moderate</span>
                    <span className="text-text-secondary">- Acceptable</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>🚫</span>
                    <span className="text-red-400">Avoid</span>
                    <span className="text-text-secondary">- Poor conditions</span>
                  </div>
                </div>
              </div>

              {/* Tip */}
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg mt-3">
                <div className="flex items-start gap-2">
                  <span className="text-lg">💡</span>
                  <div className="text-xs">
                    <span className="text-cyan-400 font-medium">Pro Tip: </span>
                    <span className="text-text-secondary">
                      Morning runs typically have better air quality as pollution levels
                      are lower before traffic peaks.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default BestRunningTimes;
